import React, { useState, useRef } from 'react';
import cx from 'classnames';
import useId from '@mc/hooks/useId';
import chainHandlers from '@mc/fn/chainHandlers';
import useDeviceContext from '@mc/editing/hooks/useDeviceContext';
import Popup from '../Popup';
import stylesheet from './Tooltip.css';

export type TooltipProps = {
  ariaStrategy?: 'label' | 'description';
  'aria-label'?: string;
  children: React.ReactNode;
  className?: string;
  direction?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  forceVisible?: boolean;
  hideTimeout?: number;
  label: React.ReactNode;
  showTimeout?: number;
  disabled?: boolean;
  /* Sets the type of the tooltip: label for shorter texts to be center-aligned, default for longer texts to be left-aligned */
  type?: 'label' | 'default';
};

/**
 * Tooltips provide contextual, helpful, but nonessential secondary information
 * upon hover or keyboard focus.
 *
 * Its child must support ref forwarding. Without it, the Tooltip will not know
 * how to position itself.
 */
function Tooltip({
  ariaStrategy,
  children,
  className,
  label,
  'aria-label': ariaLabel,
  direction = 'bottom',
  forceVisible = false,
  showTimeout = 0,
  hideTimeout = 0,
  type = 'default',
  disabled = false,
  ...props
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef();
  const delayedUpdate = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const { isMobileWeb, isMobileNative } = useDeviceContext();

  // Validates that exactly one child element is provided for the tooltip trigger.
  // Throws an error if zero or multiple children are passed.
  React.Children.only(children);
  // Cast to ReactElement since we validated with Children.only above
  const child = children as React.ReactElement;

  const setVisibility = (value: boolean, delay: number) => {
    if (delayedUpdate.current) {
      clearTimeout(delayedUpdate.current);
    }

    if (delay === 0) {
      setIsVisible(value);
    } else {
      delayedUpdate.current = setTimeout(() => {
        setIsVisible(value);
      }, delay);
    }
  };

  const show = () => {
    setVisibility(true, showTimeout);
  };

  const hide = () => {
    setVisibility(false, hideTimeout);
  };

  const shouldShow =
    (forceVisible || isVisible) && !isMobileNative && !isMobileWeb && !disabled;

  return (
    <React.Fragment>
      {React.cloneElement(child, {
        onFocus: chainHandlers(child.props.onFocus, show),
        onBlur: chainHandlers(child.props.onBlur, hide),
        onMouseEnter: chainHandlers(child.props.onMouseEnter, show),
        onMouseLeave: chainHandlers(child.props.onMouseLeave, hide),
        onClick: chainHandlers(child.props.onClick, hide),
        'aria-describedby':
          child.props['aria-describedby'] ||
          (ariaStrategy === 'description' ? id : undefined),
        'aria-labelledby':
          child.props['aria-labelledby'] ||
          (ariaStrategy === 'label' ? id : undefined),
        ref: triggerRef,
      })}
      <Popup
        arrow={<div className={stylesheet.arrow} />}
        offsetValue={14}
        placement={direction}
        targetRef={triggerRef}
        aria-label={ariaLabel}
        role="tooltip"
        aria-hidden={!shouldShow}
        className={cx(
          stylesheet.tooltipV2,
          className,
          stylesheet[type],
          shouldShow ? stylesheet.tooltipVisible : stylesheet.tooltipHidden,
        )}
        id={id}
        unsafe_key={shouldShow.toString()}
        {...props}
      >
        {label}
      </Popup>
    </React.Fragment>
  );
}

export default Tooltip;
